import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Skeleton } from '@mui/material';
import {
  LoanApplicationTabId,
  uncompletedLoanErrorMessageParams,
} from 'app/pages/loanApplication/LoanApplication.constants';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import PageBlock from 'app/shared/componentsRestyled/common/PageBlock';
import { AddBoxIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import useLoanId from 'hooks/useLoanId';
import usePendingChangeAuthors from 'hooks/usePendingChangeAuthors';

import { showMessageAction } from 'store/global/actions';
import {
  editNotesDataAction,
  showMessageThatCurrentUserCantChangeLoanAction,
} from 'store/loanApplications/actions';
import {
  selectEntityChangedPropsById,
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanApplicationEditAllowed,
  selectIsLoanCanceled,
  selectIsLoanInitiatedWithErrors,
  selectUnderwritingNotes,
} from 'store/loanApplications/selectors';

import { UnderwritingTestIds } from '../Underwriting.testIds';

import Note from './Note';
import NoteForm from './Note/NoteForm';

const Notes = ({ isLoading, setIsLoading, isParentLoading }) => {
  const dispatch = useDispatch();

  const loanId = useLoanId();

  const [isEditActive, setIsEditActive] = useState(false);

  const isLoanInitedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const underwritingNotes = useSelector(selectUnderwritingNotes);
  const pendingChanges = useSelector(selectEntityChangedPropsById(loanId));
  const pendingChangeAuthors = usePendingChangeAuthors(loanId);
  const isLoanCanceled = useSelector(selectIsLoanCanceled);
  const isEditAllowed = useSelector(selectIsLoanApplicationEditAllowed);

  const isEditDisabled = !isEditAllowed || isLoanCanceled;

  const isNoteAlreadyExist = !!underwritingNotes;

  const onEditClick = useCallback(() => {
    if (isLoanInitedWithErrors) {
      return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
    }

    if (!isCurrentUserCanChangeLoan) {
      return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
    }

    setIsEditActive(true);
  }, [isCurrentUserCanChangeLoan, isLoanInitedWithErrors, dispatch]);

  const onCancel = useCallback(() => {
    setIsEditActive(false);
  }, []);

  const onSave = useCallback(
    form => {
      setIsLoading(true);

      dispatch(
        editNotesDataAction({
          notes: form.note,
          loanId,
          requestMetadata: { tabId: LoanApplicationTabId.LOAN },
        }),
      )
        .then(() => onCancel())
        .finally(() => setIsLoading(false));
    },
    [loanId, onCancel, setIsLoading, dispatch],
  );

  const content = useMemo(() => {
    switch (true) {
      case isEditActive: {
        return (
          <NoteForm
            isLoading={isLoading}
            note={underwritingNotes}
            onCancel={onCancel}
            onSave={onSave}
          />
        );
      }

      case !!underwritingNotes: {
        return (
          <Note
            isEditActive={isEditActive}
            note={underwritingNotes}
            pendingChangeAuthors={pendingChangeAuthors}
            pendingChanges={pendingChanges}
            onEditClick={onEditClick}
          />
        );
      }

      case isParentLoading:
        return <Skeleton height={24} />;

      default:
        return <NoAvailableMessage message="No data" />;
    }
  }, [
    isLoading,
    isEditActive,
    underwritingNotes,
    isParentLoading,
    onCancel,
    onSave,
    pendingChanges,
    pendingChangeAuthors,
    onEditClick,
  ]);

  return (
    <PageBlock
      withAction
      withDivider
      actionContent={
        <Button
          data-testid={UnderwritingTestIds.NOTES_ADD_BUTTON}
          disabled={isEditDisabled || isNoteAlreadyExist || isEditActive}
          startIcon={<AddBoxIcon />}
          onClick={onEditClick}
        >
          Add
        </Button>
      }
      title="Underwriting Notes"
    >
      {content}
    </PageBlock>
  );
};

Notes.propTypes = {
  ...WithLoadingPropTypes,
  isParentLoading: PropTypes.bool,
};

export default compose(withLoading, memo)(Notes);
